// frontend/src/components/EOIR.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  FiSearch, FiEdit2,
  FiRefreshCw, FiUser, FiCalendar, FiClock,
  FiAlertCircle, FiCheckCircle, FiXCircle,
  FiTrendingUp,
  FiFolder, FiFileText,
  FiMapPin, FiBriefcase,
  FiBarChart2, FiSave, FiX, FiChevronRight, FiCopy, FiExternalLink, FiTrash2,
  FiClock as FiEADClock
} from 'react-icons/fi';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import * as eoirService from '../services/eoirService';
import { formatDateTimeUS, formatDateUS } from '../utils/dateFormat';
import './EOIR.css';
import './EOIRDetailRefinement.css';

const localizer = momentLocalizer(moment);

const EOIR = ({ onOpenClient }) => {
  const [loading, setLoading] = useState(true);
  const [cases, setCases] = useState([]);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [selectedCase, setSelectedCase] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEADModal, setShowEADModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAcisModal, setShowAcisModal] = useState(false);
  const [acisText, setAcisText] = useState('');
  const [acisPreview, setAcisPreview] = useState(null);
  const [acisLoading, setAcisLoading] = useState(false);
  const [acisError, setAcisError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('todos');
  const [clientes, setClientes] = useState([]);
  const [teamUsers, setTeamUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const [eadLoading, setEADLoading] = useState(false);
  const [calendarView, setCalendarView] = useState('month');
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [selectedView, setSelectedView] = useState('list');
  const [nationalities, setNationalities] = useState([]);
  const [eadProgress, setEADProgress] = useState(null);
  const [caseClient, setCaseClient] = useState(null);
  const [workspaceAction, setWorkspaceAction] = useState('');
  const [followUpTask, setFollowUpTask] = useState({ titulo: '', descripcion: '', fecha: '', asignado_a: '' });
  const [followUpNote, setFollowUpNote] = useState('');
  const [followUpCommunication, setFollowUpCommunication] = useState({ tipo: 'llamada', mensaje: '' });

  // Estado para consulta EAD Clock
  const [eadData, setEADData] = useState({
    a_number: '',
    nationality: '',
    cliente_id: ''
  });
  const [eadResult, setEADResult] = useState(null);

  // Estado para editar caso
  const [editData, setEditData] = useState({
    nationality: '',
    case_status: '',
    court_name: '',
    court_address: '',
    judge_name: '',
    next_hearing_date: '',
    hearing_type: '',
    hearing_time: '',
    hearing_room: '',
    attorney_name: '',
    attorney_email: '',
    case_notes: ''
  });

  const API_URL = window.__CRM_API__ || `${window.location.origin}/api`;

  // ============ FUNCIONES AUXILIARES ============
  // EOIR dates are calendar dates, not UTC timestamps. Parsing YYYY-MM-DD
  // through Date() shifts them to the previous day in western time zones.
  const parseLocalDate = (value) => {
    if (!value) return null;
    const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
    return match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : new Date(value);
  };
  const parseHearingDateTime = (day, time = '') => {
    const base = parseLocalDate(day);
    if (!base) return null;
    const match = String(time).trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
    if (!match) return base;
    let hour = Number(match[1]); const minute = Number(match[2]); const meridiem = (match[3] || '').toUpperCase();
    if (meridiem === 'PM' && hour < 12) hour += 12;
    if (meridiem === 'AM' && hour === 12) hour = 0;
    base.setHours(hour, minute, 0, 0); return base;
  };
  const plainHearingType = (value) => String(value || 'Tipo no especificado')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const hearingTypeLabel = (value) => {
    const normalized = plainHearingType(value).toLowerCase();
    if (normalized.includes('individual') || normalized.includes('merito') || normalized.includes('merit')) return 'Individual';
    if (normalized.includes('master') || normalized.includes('calendario')) return 'Master';
    return 'Tipo por confirmar';
  };
  const formatDate = (date) => {
    return formatDateUS(date, 'No disponible');
  };

  const formatDateTime = (date) => {
    return formatDateTimeUS(date, 'No disponible');
  };

  const formatDateShort = (date) => {
    return formatDateUS(date, 'N/A');
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { label: 'Pendiente', color: '#f39c12', icon: FiClock },
      active: { label: 'Activo', color: '#2ecc71', icon: FiCheckCircle },
      closed: { label: 'Cerrado', color: '#95a5a6', icon: FiXCircle },
      removed: { label: 'Removido', color: '#e74c3c', icon: FiAlertCircle },
      appealed: { label: 'Apelado', color: '#9b59b6', icon: FiTrendingUp }
    };
    const info = config[status?.toLowerCase()] || config.pending;
    const Icon = info.icon;
    return (
      <span className={`status-badge ${status?.toLowerCase() || 'pending'}`}>
        <Icon size={12} /> {info.label}
      </span>
    );
  };

  const getClienteNombre = (clienteId) => {
    const cliente = clientes.find(c => c.id === clienteId);
    return cliente ? `${cliente.nombre} ${cliente.apellido}` : 'Sin cliente';
  };

  // ============ FILTRADO ============
  const filterCases = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const filterByStatus = (status) => {
    setFilterStatus(status);
    setCurrentPage(1);
  };

  // ============ CARGAR DATOS ============
  const cargarDatos = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const [statsRes, casesRes, clientesRes, usersRes] = await Promise.all([
        eoirService.getDashboardStats(),
        eoirService.getCases(),
        axios.get(`${API_URL}/clientes/`, config),
        axios.get(`${API_URL}/auth/usuarios`, config).catch(() => ({ data: [] }))
      ]);

      setDashboardStats(statsRes);
      setCases(casesRes);
      setClientes(clientesRes.data);
      setTeamUsers(usersRes.data || []);
    } catch (error) {
      console.error('Error al cargar datos:', error);
    } finally {
      setLoading(false);
    }
  };

  // ============ CARGAR NACIONALIDADES ============
  const cargarNationalities = async () => {
    try {
      const data = await eoirService.getNationalities();
      setNationalities(data || []);
    } catch (error) {
      console.error('Error cargando nacionalidades:', error);
    }
  };

  // Initial portfolio load is intentionally performed once on mount.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    cargarDatos();
    cargarNationalities();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Casos filtrados
  const filteredCases = cases.filter(c => {
    const matchesSearch = c.a_number?.includes(searchTerm) || c.cliente_nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.nationality?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.court_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.judge_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'todos' || c.case_status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  // Paginación
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentCases = filteredCases.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredCases.length / itemsPerPage);

  // Eventos del calendario
  const calendarEvents = filteredCases
    .filter(c => c.next_hearing_date)
    .slice(0, 50)
    .map(c => ({
      id: c.id,
      title: `${c.cliente_nombre || getClienteNombre(c.cliente_id)} · ${c.a_number_display || c.a_number}${c.hearing_time ? ` · ${c.hearing_time}` : ''}`,
      start: parseHearingDateTime(c.next_hearing_date, c.hearing_time),
      end: new Date(parseHearingDateTime(c.next_hearing_date, c.hearing_time).getTime() + 60 * 60 * 1000),
      resource: c,
      className: `hearing-event ${c.case_status?.toLowerCase() || 'pending'}`,
      allDay: false,
    }));

  // ============ CONSULTAR EAD CLOCK ============
  const handleConsultEAD = async () => {
    if (!eadData.a_number) {
      alert('⚠️ Debes ingresar un A-Number');
      return;
    }
    if (!eadData.nationality) {
      alert('⚠️ Debes seleccionar la Nacionalidad');
      return;
    }

    setEADLoading(true);
    setEADResult(null);
    setEADProgress({ message: 'Iniciando consulta...', percentage: 0 });

    try {
      const result = await eoirService.consultEADClock(
        eadData.a_number,
        eadData.nationality,
        eadData.cliente_id || null,
        (message, percentage) => {
          setEADProgress({ message, percentage });
        }
      );

      if (result.success) {
        setEADResult(result);
        setEADProgress({ message: '✅ Consulta completada', percentage: 100 });
        cargarDatos();
      } else {
        setEADResult({
          success: false,
          message: result.message || 'Error desconocido',
          error: result.error
        });
        setEADProgress(null);
      }
    } catch (error) {
      console.error('Error consultando EAD Clock:', error);
      setEADResult({
        success: false,
        message: error.message || 'Error consultando el EAD Clock',
        error: error.message
      });
      setEADProgress(null);
    } finally {
      setEADLoading(false);
    }
  };

  // ============ GUARDAR CASO DESDE EAD ============
  const handleSaveCaseFromEAD = async () => {
    if (!eadResult?.data) return;
    
    try {
      const caseData = {
        a_number: eadResult.data.a_number,
        nationality: eadResult.data.nationality,
        case_status: eadResult.data.status || 'active',
        judge_name: eadResult.data.judge_name,
        court_name: eadResult.data.court_name,
        next_hearing_date: eadResult.data.next_hearing_date,
        hearing_type: eadResult.data.hearing_type,
        cliente_id: eadData.cliente_id || null
      };
      
      await eoirService.createCase(caseData);
      alert('✅ Caso guardado exitosamente');
      setShowEADModal(false);
      setEADResult(null);
      setEADData({ a_number: '', nationality: '', cliente_id: '' });
      setEADProgress(null);
      cargarDatos();
    } catch (error) {
      console.error('Error guardando caso:', error);
      alert('❌ Error al guardar el caso');
    }
  };

  // ============ EDITAR CASO ============
  const handleEditCase = (caseData) => {
    setEditData({
      nationality: caseData.nationality || '',
      case_status: caseData.case_status || '',
      court_name: caseData.court_name || '',
      court_address: caseData.court_address || '',
      judge_name: caseData.judge_name || '',
      next_hearing_date: caseData.next_hearing_date || '',
      hearing_type: (caseData.hearing_type || '').toLowerCase().includes('individual') ? 'individual' : (caseData.hearing_type || '').toLowerCase().includes('master') ? 'master' : '',
      hearing_time: caseData.hearing_time || '',
      hearing_room: caseData.hearing_room || '',
      attorney_name: caseData.attorney_name || '',
      attorney_email: caseData.attorney_email || '',
      case_notes: caseData.case_notes || ''
    });
    setSelectedCase(caseData);
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    try {
      const updated = await eoirService.updateCase(selectedCase.id, editData);
      setCases(cases.map(c => c.id === updated.id ? updated : c));
      setSelectedCase(updated);
      setShowEditModal(false);
      alert('✅ Caso actualizado exitosamente');
      cargarDatos();
    } catch (error) {
      console.error('Error al actualizar caso:', error);
      alert('❌ Error al actualizar el caso');
    }
  };
  const isFutureHearing = (value) => {
    if (!value) return false;
    const hearing = new Date(`${String(value).slice(0, 10)}T23:59:59`);
    return !Number.isNaN(hearing.getTime()) && hearing.getTime() >= Date.now();
  };

  // API_URL is derived from the immutable public runtime configuration.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!showDetailModal || !selectedCase?.cliente_id) return;
    axios.get(`${API_URL}/clientes/${selectedCase.cliente_id}`).then(response => setCaseClient(response.data)).catch(() => setCaseClient(null));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDetailModal, selectedCase?.cliente_id]);

  const refreshFollowUp = async () => {
    if (!selectedCase?.cliente_id) return;
    const response = await axios.get(`${API_URL}/clientes/${selectedCase.cliente_id}`);
    setCaseClient(response.data);
    setWorkspaceAction('');
  };

  const saveFollowUpTask = async event => {
    event.preventDefault();
    if (!selectedCase?.cliente_id) return;
    await axios.post(`${API_URL}/clientes/tareas/`, { cliente_id: selectedCase.cliente_id, ...followUpTask });
    setFollowUpTask({ titulo: '', descripcion: '', fecha: '', asignado_a: '' });
    await refreshFollowUp();
  };

  const saveFollowUpNote = async event => {
    event.preventDefault();
    if (!selectedCase?.cliente_id || !followUpNote.trim()) return;
    await axios.post(`${API_URL}/clientes/${selectedCase.cliente_id}/notas`, { contenido: followUpNote });
    setFollowUpNote(''); await refreshFollowUp();
  };

  const deleteFollowUpNote = async note => {
    if (!selectedCase?.cliente_id || !window.confirm('¿Eliminar esta nota de seguimiento?')) return;
    await axios.delete(`${API_URL}/clientes/${selectedCase.cliente_id}/notas/${note.id}`);
    await refreshFollowUp();
  };

  const saveFollowUpCommunication = async event => {
    event.preventDefault();
    if (!selectedCase?.cliente_id || !followUpCommunication.mensaje.trim()) return;
    await axios.post(`${API_URL}/clientes/comunicaciones/`, { cliente_id: selectedCase.cliente_id, ...followUpCommunication, fecha: '', hora: '', realizada_por: '' });
    setFollowUpCommunication({ tipo: 'llamada', mensaje: '' }); await refreshFollowUp();
  };

  // The legacy endpoint remains available for existing integrations. New UI uses
  // the assisted import-and-confirm flow below.
  // eslint-disable-next-line no-unused-vars
  const handleSyncCase = async (caseId) => {
    try {
      const result = await eoirService.syncCase(caseId);
      setCases(current => current.map(item => item.id === caseId ? result.case : item));
      if (selectedCase?.id === caseId) setSelectedCase(result.case);
      if (result.sync?.success) {
        alert('✅ Datos de ACIS actualizados. Verifica siempre el Notice of Hearing oficial.');
      } else {
        const reason = result.case?.data_error || 'ACIS no devolvió información pública para esta consulta en este momento.';
        alert(`⚠️ Consulta ACIS completada sin cambios. ${reason}`);
      }
    } catch (error) { alert(error.response?.data?.detail || 'No fue posible sincronizar este caso con ACIS.'); }
  };

  const normalizedAcisNumber = (value = '') => {
    const digits = String(value).replace(/\D/g, '');
    return digits.length === 8 ? `0${digits}` : digits;
  };

  const openAcisAssistant = () => {
    setAcisText('');
    setAcisPreview(null);
    setAcisError('');
    setShowAcisModal(true);
  };

  const copyAcisLookup = async () => {
    const lookup = normalizedAcisNumber(selectedCase?.a_number || selectedCase?.a_number_display);
    const content = `${lookup}\n${selectedCase?.nationality || ''}`.trim();
    try {
      await navigator.clipboard.writeText(content);
      setAcisError('Datos copiados. Pégalos en el formulario oficial de ACIS.');
    } catch (_) {
      setAcisError(`A-Number: ${lookup} · Nacionalidad: ${selectedCase?.nationality || 'Pendiente'}`);
    }
  };

  const parseAcisResult = async () => {
    if (!acisText.trim() || !selectedCase?.id) return;
    setAcisLoading(true); setAcisError('');
    try {
      const result = await eoirService.importAcisResult(selectedCase.id, acisText.trim());
      setAcisPreview({
        ...result,
        changes: Array.isArray(result.changes)
          ? result.changes
          : Object.entries(result.changes || {}).map(([field, change]) => ({
              field,
              label: field.replace(/_/g, ' '),
              current: change?.current,
              detected: change?.detected
            }))
      });
    } catch (error) {
      setAcisError(error.response?.data?.detail || 'No fue posible analizar el resultado de ACIS. Verifica que hayas pegado el texto completo.');
    } finally { setAcisLoading(false); }
  };

  const confirmAcisResult = async () => {
    if (!acisPreview?.detected || !selectedCase?.id) return;
    setAcisLoading(true); setAcisError('');
    try {
      const result = await eoirService.confirmAcisResult(selectedCase.id, acisPreview.detected);
      const updated = result.case;
      setCases(current => current.map(item => item.id === updated.id ? updated : item));
      setSelectedCase(updated);
      setShowAcisModal(false);
      await cargarDatos();
    } catch (error) {
      setAcisError(error.response?.data?.detail || 'No fue posible confirmar y sincronizar el resultado.');
    } finally { setAcisLoading(false); }
  };

  // ============ STAT CARD COMPONENT ============
  const StatCard = ({ icon: Icon, title, value, color, subtitle }) => (
    <div className="stat-card" style={{ borderLeftColor: color }}>
      <div className="stat-icon" style={{ backgroundColor: `${color}15`, color }}>
        <Icon size={20} />
      </div>
      <div className="stat-content">
        <span className="stat-value">{value}</span>
        <span className="stat-title">{title}</span>
        {subtitle && <span className="stat-subtitle">{subtitle}</span>}
      </div>
    </div>
  );

  // ============ CASE CARD COMPONENT ============
  const CaseCard = ({ case: caso, onClick }) => {
    const hasHearing = caso.next_hearing_date;
    const isEligible = caso.asylum_clock?.eligible_for_ead;

    return (
      <div className="case-card" onClick={onClick}>
        <div className="case-card-header">
          <div className="case-number-badge">
            <span className="eoir-file-icon">🗂️</span>
            <span className="case-number">{caso.a_number_display || caso.a_number}</span>
            {isEligible && <span className="ead-eligible-badge">EAD</span>}
          </div>
          {getStatusBadge(caso.case_status)}
        </div>
        <div className="case-card-body">
          <div className="case-card-row">
            <FiUser size={14} />
            <span>{caso.cliente_nombre || getClienteNombre(caso.cliente_id)}</span>
          </div>
          <div className="case-card-row">
            <FiMapPin size={14} />
            <span>{caso.court_name ? caso.court_name.split(',')[0] : 'Corte N/D'}</span>
          </div>
          <div className="case-card-row">
            <FiCalendar size={14} />
            <span className={hasHearing ? 'hearing-date' : 'no-hearing'}>
              {hasHearing ? formatDateShort(caso.next_hearing_date) : 'Sin audiencia'}
            </span>
          </div>
          <div className="case-card-row">
            <FiBriefcase size={14} />
            <span className="hearing-type">{hearingTypeLabel(caso.hearing_type)}</span>
          </div>
        </div>
        <div className="case-card-footer">
          <span className="judge-name">{caso.judge_name || 'Sin juez'} · {caso.hearing_time || 'Hora pendiente'}</span>
          <FiChevronRight className="card-arrow" />
        </div>
      </div>
    );
  };

  // ============ RENDER ============
  return (
    <div className="eoir-module">
      {/* HEADER */}
      <div className="eoir-header">
        <div className="header-left">
          <h1>⚖️ EOIR / BIA</h1>
          <p>Gestión de casos de inmigración y seguimiento EAD</p>
        </div>
        <div className="header-actions">
          <button className="btn-primary ead-btn" onClick={() => setShowEADModal(true)}>
            <FiEADClock /> Consultar caso y reloj EAD
          </button>
          <button className="btn-outline" onClick={cargarDatos} title="Actualizar datos">
            <FiRefreshCw size={18} />
          </button>
        </div>
      </div>

      {/* DASHBOARD STATS */}
      {dashboardStats && (
        <div className="eoir-stats">
          <StatCard 
            icon={FiFolder} 
            title="Total Casos" 
            value={dashboardStats.total_cases || 0} 
            color="#3498db" 
          />
          <StatCard 
            icon={FiCheckCircle} 
            title="Activos" 
            value={dashboardStats.active_cases || 0} 
            color="#2ecc71" 
          />
          <StatCard 
            icon={FiClock} 
            title="Pendientes" 
            value={dashboardStats.pending_cases || 0} 
            color="#f39c12" 
          />
          <StatCard 
            icon={FiCalendar} 
            title="Audiencias (7 días)" 
            value={dashboardStats.upcoming_hearings || 0} 
            color="#e67e22" 
          />
        </div>
      )}

      {selectedView === 'list' && (
        <div className="eoir-workspace-grid">
          <section className="attention-panel"><div className="workspace-title"><div><span>🚨</span><div><h2>Atención requerida</h2><p>Casos que requieren decisión o seguimiento hoy.</p></div></div><button onClick={() => setSelectedView('calendar')}>Ver calendario completo</button></div>{cases.filter(c => !['closed', 'archived', 'cerrado', 'archivado'].includes(String(c.case_status || '').toLowerCase()) && (isFutureHearing(c.next_hearing_date) || c.asylum_clock?.eligible_for_ead || c.asylum_clock?.clock_status === 'stopped')).slice(0,4).map(c => <button className="attention-row" key={c.id} onClick={() => {setSelectedCase(c);setShowDetailModal(true)}}><span>{isFutureHearing(c.next_hearing_date) ? '📅' : c.asylum_clock?.eligible_for_ead ? '⏰' : '⚠️'}</span><div><b>{getClienteNombre(c.cliente_id)} · {c.a_number}</b><small>{isFutureHearing(c.next_hearing_date) ? `Audiencia: ${formatDate(c.next_hearing_date)}` : c.asylum_clock?.eligible_for_ead ? 'Elegible para EAD' : 'Revisar Asylum Clock'}</small></div><FiChevronRight /></button>)}{!cases.some(c => !['closed', 'archived', 'cerrado', 'archivado'].includes(String(c.case_status || '').toLowerCase()) && (isFutureHearing(c.next_hearing_date) || c.asylum_clock?.eligible_for_ead || c.asylum_clock?.clock_status === 'stopped')) && <p className="workspace-empty">No hay alertas pendientes.</p>}</section>
          <section className="hearings-panel"><div className="workspace-title"><div><span>📅</span><div><h2>Próximas audiencias</h2><p>Las siguientes fechas de la cartera.</p></div></div></div>{cases.filter(c=>isFutureHearing(c.next_hearing_date)).sort((a,b)=>new Date(a.next_hearing_date)-new Date(b.next_hearing_date)).slice(0,4).map(c=><button className="hearing-widget" key={c.id} onClick={()=>{setSelectedCase(c);setShowDetailModal(true)}}><strong>{formatDateShort(c.next_hearing_date)}</strong><span>{getClienteNombre(c.cliente_id)}</span><small>{c.court_name||'Corte por asignar'} · {c.hearing_type||'Audiencia'}</small></button>)}</section>
        </div>
      )}

      {/* VIEW TOGGLES */}
      <div className="view-toggle-bar">
        <div className="view-toggles">
          <button 
            className={selectedView === 'calendar' ? 'active' : ''} 
            onClick={() => setSelectedView('calendar')}
          >
            <FiCalendar /> Calendario
          </button>
          <button 
            className={selectedView === 'list' ? 'active' : ''} 
            onClick={() => setSelectedView('list')}
          >
            <FiFolder /> Lista de Casos
          </button>
        </div>
        <div className="view-stats">
          <span>{filteredCases.length} casos encontrados</span>
        </div>
      </div>

      {/* CALENDARIO DE AUDIENCIAS */}
      {selectedView === 'calendar' && (
        <div className="calendar-section compact">
          <div className="calendar-wrapper">
            <Calendar
              localizer={localizer}
              events={calendarEvents}
              startAccessor="start"
              endAccessor="end"
              style={{ height: 380 }}
              view={calendarView}
              onView={setCalendarView}
              date={calendarDate}
              onNavigate={setCalendarDate}
              onSelectEvent={(event) => {
                setSelectedCase(event.resource);
                setShowDetailModal(true);
              }}
              eventPropGetter={(event) => ({
                className: event.className
              })}
              components={{
                event: ({ event }) => (
                  <div className="calendar-event-compact">
                    <span className="event-title">{event.title}</span>
                  </div>
                )
              }}
            />
          </div>
        </div>
      )}

      {/* FILTROS */}
      <div className="eoir-filters">
        <div className="search-box">
          <FiSearch className="search-icon" />
          <input
            type="text"
            placeholder="Buscar por cliente, A-Number, nacionalidad, corte o juez..."
            value={searchTerm}
            onChange={(e) => filterCases(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="filter-group">
          <select
            className="filter-select"
            value={filterStatus}
            onChange={(e) => filterByStatus(e.target.value)}
          >
            <option value="todos">Todos los estados</option>
            <option value="pending">Pendiente</option>
            <option value="active">Activo</option>
            <option value="closed">Cerrado</option>
            <option value="removed">Removido</option>
            <option value="appealed">Apelado</option>
          </select>
        </div>
      </div>

      {/* CASOS - GRID DE TARJETAS */}
      {selectedView === 'list' && (
        <div className="cases-section">
          <div className="cases-grid">
            {loading ? (
              <div className="loading-state">
                <div className="loader"></div>
                <p>Cargando casos...</p>
              </div>
            ) : currentCases.length === 0 ? (
              <div className="empty-state">
                <FiFolder className="empty-icon" />
                <h3>No hay casos</h3>
                <p>Consulta un caso usando el botón "Consultar EAD Clock"</p>
              </div>
            ) : (
              currentCases.map(caso => (
                <CaseCard
                  key={caso.id}
                  case={caso}
                  onClick={() => {
                    setSelectedCase(caso);
                    setShowDetailModal(true);
                  }}
                />
              ))
            )}
          </div>
        </div>
      )}

      {/* PAGINACIÓN */}
      {filteredCases.length > itemsPerPage && (
        <div className="pagination">
          <button 
            className="page-btn" 
            disabled={currentPage === 1} 
            onClick={() => setCurrentPage(currentPage - 1)}
          >
            Anterior
          </button>
          <span className="page-info">
            {currentPage} / {totalPages}
          </span>
          <button 
            className="page-btn" 
            disabled={currentPage === totalPages} 
            onClick={() => setCurrentPage(currentPage + 1)}
          >
            Siguiente
          </button>
        </div>
      )}

      {/* ============================================================
          MODAL EAD CLOCK - CORREGIDO CON PROGRESS
          ============================================================ */}
      {showEADModal && (
        <div className="modal-overlay" onClick={() => {
          if (!eadLoading) {
            setShowEADModal(false);
            setEADResult(null);
            setEADData({ a_number: '', nationality: '', cliente_id: '' });
            setEADProgress(null);
          }
        }}>
          <div className="modal-ead" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><FiEADClock /> Consulta ACIS y estado EAD</h2>
              <button 
                className="modal-close" 
                onClick={() => {
                  if (!eadLoading) {
                    setShowEADModal(false);
                    setEADResult(null);
                    setEADData({ a_number: '', nationality: '', cliente_id: '' });
                    setEADProgress(null);
                  }
                }}
                disabled={eadLoading}
              >
                <FiX />
              </button>
            </div>
            
            <div className="modal-body">
              {/* Campos de formulario */}
              <div className="form-group">
                <label>A-Number *</label>
                <input
                  type="text"
                  value={eadData.a_number}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^A-Za-z0-9]/g, '');
                    setEADData({...eadData, a_number: value});
                  }}
                  placeholder="Ej: A123456789 o 123456789"
                  className="ead-input"
                  disabled={eadLoading}
                />
                <small>Ingresa el A-Number (con o sin la letra A)</small>
              </div>
              
              <div className="form-group">
                <label>Nacionalidad *</label>
                <select
                  value={eadData.nationality}
                  onChange={(e) => setEADData({...eadData, nationality: e.target.value})}
                  className="ead-input"
                  disabled={eadLoading}
                >
                  <option value="">Selecciona una nacionalidad...</option>
                  {nationalities.map(nat => (
                    <option key={nat} value={nat}>{nat}</option>
                  ))}
                </select>
                <small>País de origen del solicitante</small>
              </div>

              <div className="form-group">
                <label>Cliente (Opcional)</label>
                <select
                  value={eadData.cliente_id}
                  onChange={(e) => {
                    const clientId = e.target.value;
                    const client = clientes.find(item => String(item.id) === String(clientId));
                    setEADData({
                      ...eadData,
                      cliente_id: clientId,
                      a_number: client?.a_number || eadData.a_number,
                      nationality: client?.nacionalidad || eadData.nationality
                    });
                  }}
                  disabled={eadLoading}
                >
                  <option value="">Sin cliente</option>
                  {clientes.map(c => (
                    <option key={c.id} value={c.id}>{c.nombre} {c.apellido}</option>
                  ))}
                </select>
              </div>

              <p className="ead-verification-note"><FiAlertCircle /> ACIS permite consultar datos básicos del caso. El contador y la elegibilidad EAD deben verificarse en ECAS/EOIR.</p>

              {/* Barra de progreso */}
              {eadLoading && eadProgress && (
                <div className="progress-container">
                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{ width: `${eadProgress.percentage}%` }}
                    />
                  </div>
                  <span className="progress-text">{eadProgress.message}</span>
                </div>
              )}

              {/* Botón de consulta */}
              <button 
                className="btn-primary ead-consult-btn" 
                onClick={handleConsultEAD} 
                disabled={eadLoading}
              >
                {eadLoading ? (
                  <>
                    <span className="spinner"></span>
                    Consultando...
                  </>
                ) : (
                  'Consultar caso en ACIS'
                )}
              </button>

              {/* Resultados */}
              {eadResult && !eadLoading && (
                <>
                  {eadResult.success ? (
                    <div className="ead-results">
                      <h4>Resultados de la consulta ACIS</h4>
                      {eadResult.data?._from_cache && (
                        <div className="cache-badge">📦 Datos en caché</div>
                      )}
                      <div className="ead-grid">
                        <div className="ead-item">
                          <span className="ead-label">A-Number</span>
                          <span className="ead-value">{eadResult.data?.a_number}</span>
                        </div>
                        <div className="ead-item">
                          <span className="ead-label">Días Acumulados</span>
                          <span className="ead-value highlight">{eadResult.data?.days_accumulated ?? 'Por verificar'}</span>
                        </div>
                        <div className="ead-item">
                          <span className="ead-label">Estado del Reloj</span>
                          <span className={`ead-status ${eadResult.data?.clock_status || 'unknown'}`}>
                            {eadResult.data?.clock_status === 'running' && '🟢 En ejecución'}
                            {eadResult.data?.clock_status === 'stopped' && '⏹️ Detenido'}
                            {eadResult.data?.clock_status === 'completed' && '✅ Completado'}
                            {eadResult.data?.clock_status === 'pending' && '⏳ Pendiente'}
                            {eadResult.data?.clock_status === 'unverified' && 'Verificación oficial requerida'}
                            {!eadResult.data?.clock_status && '❓ Desconocido'}
                          </span>
                        </div>
                        <div className="ead-item">
                          <span className="ead-label">Elegible para EAD</span>
                          <span className={`ead-eligible ${eadResult.data?.eligible_for_ead === true ? 'yes' : 'no'}`}>
                            {eadResult.data?.eligible_for_ead === true ? 'Sí' : eadResult.data?.eligible_for_ead === false ? 'No' : 'Por verificar'}
                          </span>
                        </div>
                        <div className="ead-item">
                          <span className="ead-label">Juez</span>
                          <span className="ead-value">{eadResult.data?.judge_name || 'No disponible'}</span>
                        </div>
                        <div className="ead-item">
                          <span className="ead-label">Corte</span>
                          <span className="ead-value">{eadResult.data?.court_name || 'No disponible'}</span>
                        </div>
                        <div className="ead-item">
                          <span className="ead-label">Próxima Audiencia</span>
                          <span className="ead-value">{formatDate(eadResult.data?.next_hearing_date) || 'No programada'}</span>
                        </div>
                        <div className="ead-item">
                          <span className="ead-label">Estado del Caso</span>
                          <span className="ead-value">{eadResult.data?.status || 'Desconocido'}</span>
                        </div>
                      </div>
                      {eadResult.data?.ead_notice && <p className="ead-verification-note"><FiAlertCircle /> {eadResult.data.ead_notice}</p>}
                      <div className="ead-actions">
                        <button 
                          className="btn-save" 
                          onClick={handleSaveCaseFromEAD}
                        >
                          <FiSave /> Guardar Caso
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="ead-error">
                      <FiAlertCircle size={20} color="#e74c3c" />
                      <span>{eadResult.message || eadResult.error || 'Error desconocido'}</span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ============================================================
          MODAL DETALLE
          ============================================================ */}
      {showDetailModal && selectedCase && (
        <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="modal-detail" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📋 {selectedCase.cliente_nombre || getClienteNombre(selectedCase.cliente_id)} · {selectedCase.a_number_display || selectedCase.a_number}</h2>
              <button className="modal-close" onClick={() => setShowDetailModal(false)}>
                <FiX />
              </button>
            </div>
            <div className="modal-body">
              {/* Información del Caso */}
              <div className="detail-section">
                <h3><FiBriefcase /> Información del Caso</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="label">A-Number</span>
                    <span className="value">{selectedCase.a_number_display || selectedCase.a_number}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Nacionalidad</span>
                    <span className="value">{selectedCase.nationality || 'No disponible'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Estado</span>
                    <span className="value">{getStatusBadge(selectedCase.case_status)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Corte</span>
                    <span className="value">{selectedCase.court_name || 'No disponible'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Juez</span>
                    <span className="value">{selectedCase.judge_name || 'Sin asignar'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Cliente</span>
                    <span className="value">{selectedCase.cliente_nombre || getClienteNombre(selectedCase.cliente_id)}</span>
                  </div>
                </div>
              </div>

              {/* Próxima Audiencia */}
              <div className="detail-section eoir-hearing-priority">
                <h3><FiCalendar /> Próxima Audiencia</h3>
                <div className="hearing-card">
                  <div className="hearing-date">
                    <span className="date">{formatDate(selectedCase.next_hearing_date)}</span>
                    <span className="time">{selectedCase.hearing_time || ''}</span>
                  </div>
                  <div className="hearing-details">
                    <span className="type">{hearingTypeLabel(selectedCase.hearing_type)}</span>
                    <span className="room">Sala: {selectedCase.hearing_room || 'No especificada'}</span>
                    <span className="room">Corte: {selectedCase.court_name || 'Por confirmar'}</span>
                    <span className="room">Dirección: {selectedCase.court_address || 'No publicada por ACIS / por confirmar'}</span>
                    <span className="room">Juez: {selectedCase.judge_name || 'Por confirmar'}</span>
                  </div>
                </div>
              </div>

              {/* Asylum Clock */}
              <div className="detail-section clock-section">
                <h3><FiClock /> Asylum Clock</h3>
                <div className="clock-display">
                  <div className="clock-days">
                    <span className="days-number">{selectedCase.asylum_clock?.days_accumulated ?? 'Por verificar'}</span>
                    <span className="days-label">días acumulados</span>
                  </div>
                  <div className="clock-status">
                    <span className={`clock-status-badge ${selectedCase.asylum_clock?.clock_status || 'running'}`}>
                      {selectedCase.asylum_clock?.clock_status === 'running' && '🟢 En ejecución'}
                      {selectedCase.asylum_clock?.clock_status === 'stopped' && '⏹️ Detenido'}
                      {selectedCase.asylum_clock?.clock_status === 'completed' && '✅ Completado'}
                      {selectedCase.asylum_clock?.clock_status === 'pending' && '⏳ Pendiente'}
                    </span>
                    {selectedCase.asylum_clock?.eligible_for_ead && (
                      <span className="ead-badge">✅ Elegible para EAD</span>
                    )}
                  </div>
                </div>
                <div className="clock-timeline">
                  <span>Inicio: {formatDate(selectedCase.asylum_clock?.start_date)}</span>
                  <span>Última actualización: {formatDateTime(selectedCase.asylum_clock?.last_updated)}</span>
                </div>
              </div>

              {/* Estadísticas del Juez */}
              {selectedCase.judge_statistics && (
                <div className="detail-section">
                  <h3><FiBarChart2 /> Estadísticas del Juez</h3>
                  <div className="judge-stats-container">
                    <div className="stat-bar-container">
                      <div className="stat-bar-label">
                        <span>Aprobación</span>
                        <span>{selectedCase.judge_statistics.grant_rate || 0}%</span>
                      </div>
                      <div className="stat-bar-track">
                        <div className="stat-bar-fill approval" style={{ width: `${selectedCase.judge_statistics.grant_rate || 0}%` }} />
                      </div>
                    </div>
                    <div className="stat-bar-container">
                      <div className="stat-bar-label">
                        <span>Negación</span>
                        <span>{selectedCase.judge_statistics.denial_rate || 0}%</span>
                      </div>
                      <div className="stat-bar-track">
                        <div className="stat-bar-fill denial" style={{ width: `${selectedCase.judge_statistics.denial_rate || 0}%` }} />
                      </div>
                    </div>
                    <div className="stat-meta">
                      <span>Casos analizados: {selectedCase.judge_statistics.total_cases || 0}</span>
                      <span>Fuente: {selectedCase.judge_statistics.data_source || 'TRAC'}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Notas */}
              {selectedCase.case_notes && (
                <div className="detail-section">
                  <h3><FiFileText /> Notas</h3>
                  <p className="case-notes">{selectedCase.case_notes}</p>
                </div>
              )}
              <section className={`eoir-followup-workspace ${workspaceAction ? 'is-expanded' : ''}`} data-action={workspaceAction || 'overview'}>
                <div className="eoir-followup-actions" aria-label="Acciones del expediente"><button type="button" onClick={() => selectedCase.cliente_id ? setWorkspaceAction('task') : alert('Vincula primero este expediente a un cliente para crear tareas.')}>✅ Tareas <small>{caseClient?.tareas?.filter(item => item.estado !== 'completada').length || 0}</small></button><button type="button" onClick={() => selectedCase.cliente_id ? setWorkspaceAction('note') : alert('Vincula primero este expediente a un cliente para guardar notas.')}>📝 Notas <small>{(caseClient?.actividad || []).filter(item => item.accion?.startsWith('Nota de seguimiento:')).length || 0}</small></button><button type="button" onClick={() => selectedCase.cliente_id ? setWorkspaceAction('communication') : alert('Vincula primero este expediente a un cliente para registrar comunicaciones.')}>💬 Comunicaciones <small>{caseClient?.comunicaciones?.length || 0}</small></button></div>
                {workspaceAction === 'task' && <form className="eoir-action-form" onSubmit={saveFollowUpTask}><header className="eoir-action-dialog-header"><div><span>✅</span><div><h3>Tareas del expediente</h3><p>Revise las tareas abiertas o agregue una nueva.</p></div></div><button type="button" aria-label="Cerrar tareas" onClick={() => setWorkspaceAction('')}><FiX /></button></header><div className="eoir-action-records">{(caseClient?.tareas || []).slice(0, 8).map(item => <article key={item.id}><b>{item.titulo}</b><small>{item.estado || 'pendiente'} · {item.fecha || 'Sin fecha'}</small></article>)}{!(caseClient?.tareas || []).length && <p>No hay tareas registradas todavía.</p>}</div><label>Tarea<input required value={followUpTask.titulo} onChange={event => setFollowUpTask({ ...followUpTask, titulo: event.target.value })} placeholder="Ej. Revisar Notice of Hearing" /></label><label>Responsable<select required value={followUpTask.asignado_a} onChange={event => setFollowUpTask({ ...followUpTask, asignado_a: event.target.value })}><option value="">Seleccionar usuario…</option>{teamUsers.filter(user => user.activo !== false).map(user => <option key={user.id} value={`${user.nombre} ${user.apellido}`}>{user.nombre} {user.apellido}</option>)}</select></label><label>Fecha límite<input required type="date" value={followUpTask.fecha} onChange={event => setFollowUpTask({ ...followUpTask, fecha: event.target.value })} /></label><label className="wide">Descripción<textarea value={followUpTask.descripcion} onChange={event => setFollowUpTask({ ...followUpTask, descripcion: event.target.value })} placeholder="Instrucciones y contexto de seguimiento" /></label><footer><button type="button" onClick={() => setWorkspaceAction('')}>Cancelar</button><button>Guardar tarea</button></footer></form>}
                {workspaceAction === 'note' && <form className="eoir-action-form single" onSubmit={saveFollowUpNote}><header className="eoir-action-dialog-header"><div><span>📝</span><div><h3>Notas de seguimiento</h3><p>Historial del cliente vinculado al expediente.</p></div></div><button type="button" aria-label="Cerrar notas" onClick={() => setWorkspaceAction('')}><FiX /></button></header><div className="eoir-action-records">{(caseClient?.actividad || []).filter(item => item.accion?.startsWith('Nota de seguimiento:')).slice(0, 8).map(item => <article key={item.id}><div><b>{item.accion.replace('Nota de seguimiento:', '').trim()}</b><small>{item.fecha} · {item.usuario || 'Sistema'}</small></div><button type="button" className="eoir-note-delete" title="Eliminar nota" onClick={() => deleteFollowUpNote(item)}><FiTrash2 /></button></article>)}{!(caseClient?.actividad || []).some(item => item.accion?.startsWith('Nota de seguimiento:')) && <p>No hay notas registradas todavía.</p>}</div><label className="wide">Nueva nota<textarea required value={followUpNote} onChange={event => setFollowUpNote(event.target.value)} placeholder="Registre el seguimiento, decisión o próximo paso…" /></label><footer><button type="button" onClick={() => setWorkspaceAction('')}>Cancelar</button><button>Guardar nota</button></footer></form>}
                {workspaceAction === 'communication' && <form className="eoir-action-form single" onSubmit={saveFollowUpCommunication}><header className="eoir-action-dialog-header"><div><span>💬</span><div><h3>Comunicaciones</h3><p>Registro compartido con el perfil del cliente.</p></div></div><button type="button" aria-label="Cerrar comunicaciones" onClick={() => setWorkspaceAction('')}><FiX /></button></header><div className="eoir-action-records">{(caseClient?.comunicaciones || []).slice(0, 8).map(item => <article key={item.id}><b>{item.tipo || 'Comunicación'} · {item.mensaje || item.detalle || 'Sin detalle'}</b><small>{item.fecha || 'Sin fecha'} · {item.realizada_por || 'Sistema'}</small></article>)}{!(caseClient?.comunicaciones || []).length && <p>No hay comunicaciones registradas todavía.</p>}</div><label>Canal<select value={followUpCommunication.tipo} onChange={event => setFollowUpCommunication({ ...followUpCommunication, tipo: event.target.value })}><option value="llamada">📞 Llamada</option><option value="whatsapp">💬 WhatsApp</option><option value="email">✉️ Correo electrónico</option><option value="mensaje_texto">📱 Mensaje de texto</option><option value="carta">📄 Carta</option></select></label><label className="wide">Mensaje o resultado<textarea required value={followUpCommunication.mensaje} onChange={event => setFollowUpCommunication({ ...followUpCommunication, mensaje: event.target.value })} placeholder="Resumen de la comunicación con el cliente" /></label><p className="eoir-author-note">🛡️ El CRM asignará automáticamente fecha, hora y usuario autenticado.</p><footer><button type="button" onClick={() => setWorkspaceAction('')}>Cancelar</button><button>Registrar comunicación</button></footer></form>}
                <div className="eoir-followup-history"><article><b>✅ Tareas</b><span>{caseClient?.tareas?.filter(item => item.estado !== 'completada').length || 0} abiertas</span><small>{caseClient?.tareas?.slice(0, 2).map(item => item.titulo).join(' · ') || 'Sin tareas pendientes'}</small></article><article><b>💬 Comunicaciones</b><span>{caseClient?.comunicaciones?.length || 0} registradas</span><small>{caseClient?.comunicaciones?.slice(0, 1).map(item => `${item.tipo} · ${item.realizada_por || 'Sistema'}`).join('') || 'Sin comunicaciones'}</small></article><article><b>📝 Actividad</b><span>{caseClient?.actividad?.length || 0} movimientos</span><small>{caseClient?.actividad?.slice(0, 1).map(item => item.accion).join('') || 'Sin actividad reciente'}</small></article></div>
                <div className="eoir-followup-notes"><b>📝 Notas de seguimiento recientes</b>{(caseClient?.actividad || []).filter(item => item.accion?.startsWith('Nota de seguimiento:')).slice(0, 3).map(item => <article key={item.id}><span>{item.accion.replace('Nota de seguimiento:', '').trim()}</span><small>{item.fecha} · {item.usuario || 'Sistema'}</small></article>)}{!(caseClient?.actividad || []).some(item => item.accion?.startsWith('Nota de seguimiento:')) && <small className="eoir-empty-note">Aún no hay notas de seguimiento.</small>}</div>
              </section>
            </div>
            <div className="modal-footer">
              {selectedCase.cliente_id && <button className="btn-edit" onClick={() => onOpenClient?.(selectedCase.cliente_id)}>
                <FiUser /> Abrir expediente del cliente
              </button>}
              <a className="btn-edit acis-manual-link" href="https://acis.eoir.justice.gov/es/caseinformation/" target="_blank" rel="noreferrer">
                🛡 Verificar ACIS manualmente
              </a>
              <button className="btn-close" onClick={() => setShowDetailModal(false)}>
                Cerrar
              </button>
              <button className="btn-edit" onClick={() => {
                setShowDetailModal(false);
                handleEditCase(selectedCase);
              }}>
                <FiEdit2 /> Editar
              </button>
              <button className="btn-edit" onClick={openAcisAssistant}>
                <FiRefreshCw /> Consultar en ACIS
              </button>
            </div>
          </div>
        </div>
      )}

      {showAcisModal && selectedCase && (
        <div className="modal-overlay eoir-acis-overlay" onClick={() => !acisLoading && setShowAcisModal(false)}>
          <section className="modal-edit eoir-acis-modal" onClick={(event) => event.stopPropagation()} aria-modal="true" role="dialog" aria-labelledby="acis-modal-title">
            <header className="modal-header eoir-acis-header">
              <div>
                <span className="eoir-acis-eyebrow">EOIR ACIS · VERIFICACIÓN ASISTIDA</span>
                <h2 id="acis-modal-title">Consultar e importar resultado</h2>
                <p>El CRM solo guarda los datos después de que los revise y confirme.</p>
              </div>
              <button className="modal-close" aria-label="Cerrar" onClick={() => setShowAcisModal(false)}><FiX /></button>
            </header>
            <div className="modal-body eoir-acis-body">
              <div className="eoir-acis-lookup">
                <div><span>A-Number normalizado</span><strong>{normalizedAcisNumber(selectedCase.a_number || selectedCase.a_number_display) || 'Pendiente'}</strong></div>
                <div><span>Nacionalidad</span><strong>{selectedCase.nationality || 'Pendiente de registrar'}</strong></div>
                <button type="button" className="btn-edit" onClick={copyAcisLookup}><FiCopy /> Copiar datos</button>
                <button type="button" className="btn-primary" onClick={() => window.open('https://acis.eoir.justice.gov/es/caseInformation', '_blank', 'noopener,noreferrer')}><FiExternalLink /> Abrir ACIS</button>
              </div>
              {!acisPreview ? <>
                <label className="eoir-acis-textarea-label">Pegar resultado de ACIS
                  <textarea value={acisText} onChange={(event) => setAcisText(event.target.value)} placeholder="Pegue aquí el texto completo de la página de resultado de ACIS en español o inglés." rows="10" />
                </label>
                <p className="eoir-acis-help">No se realiza scraping ni se guarda el HTML completo. Se extraen solo los datos operativos del expediente.</p>
                {acisError && <p className="eoir-acis-message">{acisError}</p>}
                <div className="modal-footer eoir-acis-footer"><button type="button" className="btn-close" onClick={() => setShowAcisModal(false)}>Cancelar</button><button type="button" className="btn-primary" disabled={acisLoading || !acisText.trim()} onClick={parseAcisResult}>{acisLoading ? 'Analizando…' : 'Pegar resultado y comparar'}</button></div>
              </> : <>
                <div className="eoir-acis-comparison">
                  <div className="eoir-acis-comparison-title"><h3>Comparación de datos</h3><button type="button" className="btn-edit" onClick={() => setAcisPreview(null)}>Editar resultado</button></div>
                  {(acisPreview.changes || []).length ? <div className="eoir-acis-changes">{acisPreview.changes.map((change, index) => <article key={`${change.field}-${index}`}><span>{change.label || change.field}</span><del>{change.current || 'Sin registrar'}</del><strong>{change.detected || 'Sin dato'}</strong></article>)}</div> : <div className="eoir-acis-empty">No se detectaron cambios frente al expediente. Puede confirmar para registrar la verificación.</div>}
                  <div className="eoir-acis-detected"><h4>Datos detectados</h4>{Object.entries(acisPreview.detected || {}).filter(([, value]) => value).map(([key, value]) => <p key={key}><span>{key.replace(/_/g, ' ')}</span><b>{String(value)}</b></p>)}</div>
                </div>
                {acisError && <p className="eoir-acis-message">{acisError}</p>}
                <div className="modal-footer eoir-acis-footer"><button type="button" className="btn-close" onClick={() => setShowAcisModal(false)}>Cancelar</button><button type="button" className="btn-primary" disabled={acisLoading} onClick={confirmAcisResult}>{acisLoading ? 'Sincronizando…' : 'Confirmar y sincronizar'}</button></div>
              </>}
            </div>
          </section>
        </div>
      )}

      {/* ============================================================
          MODAL EDITAR
          ============================================================ */}
      {showEditModal && selectedCase && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-edit" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>✏️ Editar Caso {selectedCase.a_number}</h2>
              <button className="modal-close" onClick={() => setShowEditModal(false)}>
                <FiX />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label>Nacionalidad</label>
                  <input
                    type="text"
                    value={editData.nationality}
                    onChange={(e) => setEditData({...editData, nationality: e.target.value})}
                    placeholder="Ej: Colombia"
                  />
                </div>
                <div className="form-group">
                  <label>Estado</label>
                  <select
                    value={editData.case_status}
                    onChange={(e) => setEditData({...editData, case_status: e.target.value})}
                  >
                    <option value="pending">Pendiente</option>
                    <option value="active">Activo</option>
                    <option value="closed">Cerrado</option>
                    <option value="removed">Removido</option>
                    <option value="appealed">Apelado</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Corte</label>
                  <input
                    type="text"
                    value={editData.court_name}
                    onChange={(e) => setEditData({...editData, court_name: e.target.value})}
                    placeholder="Ej: Los Angeles Immigration Court"
                  />
                </div>
                <div className="form-group">
                  <label>Juez</label>
                  <input
                    type="text"
                    value={editData.judge_name}
                    onChange={(e) => setEditData({...editData, judge_name: e.target.value})}
                    placeholder="Ej: Judge Ira E. Bank"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Dirección de la Corte</label>
                <input
                  type="text"
                  value={editData.court_address}
                  onChange={(e) => setEditData({...editData, court_address: e.target.value})}
                  placeholder="Dirección completa"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Próxima Audiencia</label>
                  <input
                    type="date"
                    value={editData.next_hearing_date}
                    onChange={(e) => setEditData({...editData, next_hearing_date: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Tipo de Audiencia</label>
                  <select
                    value={editData.hearing_type}
                    onChange={(e) => setEditData({...editData, hearing_type: e.target.value})}
                  >
                    <option value="">Seleccionar tipo…</option>
                    <option value="master">Master</option>
                    <option value="individual">Individual</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Hora</label>
                  <input
                    type="text"
                    value={editData.hearing_time}
                    onChange={(e) => setEditData({...editData, hearing_time: e.target.value})}
                    placeholder="Ej: 09:30 AM"
                  />
                </div>
                <div className="form-group">
                  <label>Sala</label>
                  <input
                    type="text"
                    value={editData.hearing_room}
                    onChange={(e) => setEditData({...editData, hearing_room: e.target.value})}
                    placeholder="Ej: Courtroom 3"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Abogado</label>
                  <input
                    type="text"
                    value={editData.attorney_name}
                    onChange={(e) => setEditData({...editData, attorney_name: e.target.value})}
                    placeholder="Nombre del abogado"
                  />
                </div>
                <div className="form-group">
                  <label>Email del Abogado</label>
                  <input
                    type="email"
                    value={editData.attorney_email}
                    onChange={(e) => setEditData({...editData, attorney_email: e.target.value})}
                    placeholder="abogado@email.com"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Notas</label>
                <textarea
                  value={editData.case_notes}
                  onChange={(e) => setEditData({...editData, case_notes: e.target.value})}
                  rows="4"
                  placeholder="Notas adicionales del caso..."
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowEditModal(false)}>
                Cancelar
              </button>
              <button className="btn-save" onClick={handleSaveEdit}>
                <FiSave /> Guardar Cambios
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EOIR;

